import React, { useEffect, useMemo } from 'react';
import OrgOnboardingPage from './OrgOnboardingPage';
import onboardingMessages from '@/messages/onboarding';
import { Box, Center, HStack, Image, Text, Tooltip } from '@chakra-ui/react';
import { TABLE_COLUMNS_ORG_ROSTER_REGISTER } from '@/utils/tableConfigs';
import { TParsedRosterList, useOrganizationOnboardingStore } from '@/store/useOrganizationOnboardingStore';
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import { USER_AFFILIATION_OPTIONS } from '@/constants';
import CustomTable from '@/components/molecules/CustomTable';
import { TUserAffiliation } from '@/types/User.types';
import { isUserMarkedAsPeerSupport } from '@/utils/onboardingUtils';
import iconsPng from '@/assets/img/png/icons';
import type { InviteUsersFailedRowDto } from '@/types/generated/schemas';
import RosterErrorSummary from '@/components/organization/RosterErrorSummary';
import { getInviteFailureReason } from '@/utils/inviteFailures';

type TSortingColumn = string;

type Props = {
  onGoBack?: () => void;
  onSubmit?: () => void;
  isDisabledSubmit?: boolean;
  isLoading?: boolean;
  rosterList?: TParsedRosterList;
  headerHidden?: boolean;
  failedRows?: InviteUsersFailedRowDto[];
  invited?: number;
  showInvitedOnly?: boolean;
};

const RosterPreview = ({
  onGoBack = () => {},
  onSubmit = () => {},
  isDisabledSubmit = false,
  isLoading = false,
  rosterList,
  headerHidden,
  failedRows = [],
  invited,
  showInvitedOnly = false,
}: Props) => {
  const { rosterCSV, rosterManual, selectedRosterTab } = useOrganizationOnboardingStore();

  const finalRoster = rosterList ? rosterList : selectedRosterTab === 'csv' ? rosterCSV : rosterManual;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const rosterRowIndexesWithErrors = useMemo(() => {
    const rowIndexesWithErrors: number[] = [];
    finalRoster?.forEach((rosterItem, index) => {
      if (!USER_AFFILIATION_OPTIONS.includes(rosterItem.Affiliation.toLowerCase() as TUserAffiliation)) {
        rowIndexesWithErrors.push(index);
      }
    });
    return rowIndexesWithErrors;
  }, [finalRoster]);

  const hasInviteFailures = failedRows.length > 0;
  const isInvitedList = hasInviteFailures && showInvitedOnly && invited !== undefined;
  const isDisabledSubmitFinal = isDisabledSubmit || (!hasInviteFailures && rosterRowIndexesWithErrors.length > 0);
  const issuesByRow = new Map<number, string[]>();

  rosterRowIndexesWithErrors.forEach((index) => {
    issuesByRow.set(index, [`Invalid Affiliation. Use ${USER_AFFILIATION_OPTIONS.join(', ')}.`]);
  });

  failedRows.forEach((row) => {
    issuesByRow.set(row.rowIndex, [...(issuesByRow.get(row.rowIndex) ?? []), getInviteFailureReason(row)]);
  });

  const rosterIssues = [...issuesByRow.entries()]
    .sort(([a], [b]) => a - b)
    .map(([index, reasons]) => {
      const member = finalRoster?.[index];
      const failedRow = failedRows.find((row) => row.rowIndex === index);
      const name = [member?.['First Name'], member?.['Last Name']].filter(Boolean).join(' ');
      return {
        label: name || member?.Email || failedRow?.email || (failedRow ? 'Member' : `Row ${index + 1}`),
        reason: reasons.join(' '),
        rowIndex: failedRow?.rowIndex,
      };
    });

  const failedRowIndexes = new Set(failedRows.map((row) => row.rowIndex));
  const rosterItemsWithId = (finalRoster ?? [])
    .map((rosterItem, index) => ({ ...rosterItem, id: String(index) }))
    .filter((_, index) => !hasInviteFailures || (isInvitedList && !failedRowIndexes.has(index)));

  const tableListItems = useMemo(
    () =>
      rosterItemsWithId.map((item: (typeof rosterItemsWithId)[number]) => ({
        id: item.id,
        cells: TABLE_COLUMNS_ORG_ROSTER_REGISTER.map((column) => {
          if (column.label === 'PEER SUPPORT') {
            return (
              <HStack>
                <Text fontWeight={600}>{isUserMarkedAsPeerSupport(item['Peer Support']) ? 'Yes' : 'No'}</Text>
                {item['Peer Support Lead'] ? (
                  <Tooltip
                    label={'Peer Support Lead'}
                    color={'text.darkBlue'}
                    fontWeight={600}
                    background={'white'}
                    maxWidth={'140px'}
                    borderRadius={'8px'}
                    padding={'10px'}
                    placement="bottom-start"
                    left={5}
                  >
                    <Image
                      alt="peer-support-lead"
                      src={iconsPng.peerSupportLead}
                      width={4}
                      height={5}
                      opacity={item['Peer Support Lead'] ? 0.5 : 0}
                      _hover={{ opacity: item['Peer Support Lead'] ? 1 : 0 }}
                    />
                  </Tooltip>
                ) : null}
              </HStack>
            );
          } else {
            return column.renderCell(item);
          }
        }),
      })),
    [rosterItemsWithId],
  );

  return (
    <OrgOnboardingPage
      title={
        hasInviteFailures
          ? isInvitedList && invited > 0
            ? 'Invited members'
            : 'Invitation results'
          : onboardingMessages.previewYourRosterTitle
      }
      subTitle={
        hasInviteFailures
          ? invited === 0
            ? 'No members were invited.'
            : isInvitedList
              ? 'These members were invited successfully.'
              : 'We could not confirm which members were invited.'
          : onboardingMessages.previewYourRosterSubtitle
      }
      description={
        hasInviteFailures
          ? 'Members who were not invited are listed below the table.'
          : onboardingMessages.previewYourRosterDescription
      }
      onGoBack={onGoBack}
      onSubmit={onSubmit}
      submitLabel={
        hasInviteFailures
          ? invited !== undefined
            ? `Continue with ${invited} valid row${invited > 1 ? 's' : ''}`
            : 'Continue'
          : onboardingMessages.looksGood
      }
      isSubmitDisabled={isDisabledSubmitFinal}
      isSubmitDanger={failedRows.length > 0}
      isSubmitLoading={isLoading}
      hideSeparator={true}
      headerHidden={headerHidden}
    >
      <Box width={MAX_CONTAINER_WIDTH} marginY={'50px'}>
        {rosterItemsWithId.length > 0 && (
          <Center>
            <CustomTable<typeof rosterItemsWithId, TSortingColumn>
              dataWithId={rosterItemsWithId}
              tableListItems={tableListItems}
              customColumnGridPercentages={[13, 15, 13, 30, 17, 12]}
              columns={TABLE_COLUMNS_ORG_ROSTER_REGISTER.map((column) => column.label)}
            />
          </Center>
        )}
        <RosterErrorSummary issues={rosterIssues} afterInvite={hasInviteFailures} />
      </Box>
    </OrgOnboardingPage>
  );
};

export default RosterPreview;
